import { useEffect, useState } from "react";

import type { RiskPictureItem } from "./risk-picture-model";

export const MARKER_PREVIEW_FADE_MS = 150;

export interface FadingMarkerPreview {
  item: RiskPictureItem;
  opacity: number;
  leaving?: boolean;
}

/** Preserve outgoing cards until their opacity transition finishes, including rapid changes. */
export const transitionMarkerPreviews = (
  current: FadingMarkerPreview[],
  next: RiskPictureItem | undefined,
): FadingMarkerPreview[] => {
  const result = current.map((entry): FadingMarkerPreview =>
    entry.item.id === next?.id
      ? { item: next, opacity: 1 }
      : { item: entry.item, opacity: 0, leaving: true },
  );
  if (next && !current.some((entry) => entry.item.id === next.id)) {
    result.push({ item: next, opacity: 0 });
  }
  return result;
};

/** Start newly mounted cards' fade-in after their transparent frame has painted. */
export const revealMarkerPreviews = (
  current: FadingMarkerPreview[],
): FadingMarkerPreview[] =>
  current.map((entry) => (entry.leaving ? entry : { ...entry, opacity: 1 }));

/** Drop cards only after their fade-out completes. */
export const dropMarkerPreviews = (current: FadingMarkerPreview[]): FadingMarkerPreview[] =>
  current.filter((entry) => !entry.leaving);

/** Retain each warning's own marker position and content during preview crossfades. */
export const useFadingMarkerPreview = (
  item: RiskPictureItem | undefined,
): FadingMarkerPreview[] => {
  const [entries, setEntries] = useState<FadingMarkerPreview[]>([]);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setEntries(item ? [{ item, opacity: 1 }] : []);
      return;
    }
    setEntries((current) => transitionMarkerPreviews(current, item));
    let revealFrame: number | undefined;
    // Two frames ensure the incoming DOM has painted at opacity 0 before revealing it.
    const frame = requestAnimationFrame(() => {
      revealFrame = requestAnimationFrame(() => setEntries(revealMarkerPreviews));
    });
    const timer = window.setTimeout(
      () => setEntries(dropMarkerPreviews),
      MARKER_PREVIEW_FADE_MS + 50,
    );
    return () => {
      cancelAnimationFrame(frame);
      if (revealFrame !== undefined) cancelAnimationFrame(revealFrame);
      window.clearTimeout(timer);
    };
  }, [item]);
  return entries;
};
